import { IComponent, ILink, LOAD_PICASSO_STATE as LOAD_WORKFLOW_ENTITIES } from "@multiweb/picasso";
import {
    CLEAR_PICASSO_HOST,
    CREATE_TRANSITION,
    DELETE_SELECTED_WF_ENTITIES,
    DELETE_SET,
    DIALOG_CANCELED,
    DIALOG_CONFIRMED,
    ENABLE_STATUS_VIEW,
    ENABLE_VIEWER_MODE,
    HIDE_MESSAGE_WINDOW,
    INIT_PICASSO_HOST,
    LOAD_ACTIVITY_HEADER,
    LOAD_TRANSITION_HEADER,
    SET_TOUCHED,
    SHOW_MESSAGE_WINDOW,
    TOGGLE_EDIT_DIALOGS_VISIBILITY,
    TOGGLE_GRID,
    UPDATE_ACTIVITY,
    UPDATE_ACTIVITY_PROPERTY_PANEL_STATE,
    UPDATE_DEFINITION_PARTICIPANTS,
    UPDATE_GENERAL_PROPERTY_PANEL_STATE,
    UPDATE_SETS,
    UPDATE_TRANSITION,
    UPDATE_TRANSITION_PROPERTY_PANEL_STATE,
    UPDATE_WORKFLOW_VIEW,
} from "./constants";
import {
    ActivityPropertyContainers,
    GeneralPropertyContainer,
    IActivity,
    IClearPicassoHostAction,
    ICreateTransitionAction,
    IDeleteSelectedWfEntitiesAction,
    IDeleteSetAction,
    IDialogCanceledAction,
    IDialogConfirmedAction,
    IEnableStatusViewAction,
    IEnableViewerModeAction,
    IHideMessageWindowAction,
    IInitPicassoHostAction,
    ILoadActivityHeaderAction,
    ILoadTransitionHeaderAction,
    ILoadWorkflowEntities,
    IParticipant,
    ISet,
    ISetTouchedAction,
    IShowMessageWindowAction,
    IToggleEditDialogsVisibilityAction,
    IToggleGridAction,
    ITransition,
    IUpdateActivityAction,
    IUpdateActivityPropertyPanelStateAction,
    IUpdateDefinitionParticipantsAction,
    IUpdateGeneralPropertyPanelStateAction,
    IUpdateSetsAction,
    IUpdateTransitionAction,
    IUpdateTransitionPropertyPanelStateAction,
    IUpdateWorkflowViewAction,
    IVariable,
    TransitionPropertyContainers,
} from "./types";

/** Inicializační akce, která vytvoří instanci WF editor storu. */
export const createInitPicassoHostAction = (hostKey: string): IInitPicassoHostAction => ({
    type: INIT_PICASSO_HOST,
    meta: {
        hostKey,
    },
});

/** Akce starající se o smazání instance WF editoru ze storu. */
export const createClearPicassoHostAction = (hostKey: string): IClearPicassoHostAction => ({
    type: CLEAR_PICASSO_HOST,
    meta: {
        hostKey,
    },
});

/** Zapne režim Zobrazení stavu WF. */
export const createEnableStatusViewAction = (hostKey: string, variables: readonly IVariable[]): IEnableStatusViewAction => ({
    type: ENABLE_STATUS_VIEW,
    payload: {
        variables,
    },
    meta: {
        hostKey,
    },
});

/** Zapne readonly režim editoru WF. */
export const createEnableViewerModeAction = (hostKey: string): IEnableViewerModeAction => ({
    type: ENABLE_VIEWER_MODE,
    meta: {
        hostKey,
    },
});

/** Přepne viditelnost gridu na plátně. */
export const createToggleGridAction = (hostKey: string): IToggleGridAction => ({
    type: TOGGLE_GRID,
    meta: {
        hostKey,
    },
});

/** Přepne viditelnost všech editačních dialogů. */
export const createToggleEditDialogsVisibilityAction = (hostKey: string): IToggleEditDialogsVisibilityAction => ({
    type: TOGGLE_EDIT_DIALOGS_VISIBILITY,
    meta: {
        hostKey,
    },
});

/**
 * Nastaví hodnotu indikující, zdali WF bylo nějak editováno.
 * @param hostKey
 * @param touched Pokud true, tak wf bylo editováno.
 */
export const createSetTouchedAction = (hostKey: string, touched: boolean): ISetTouchedAction => ({
    type: SET_TOUCHED,
    payload: {
        touched,
    },
    meta: {
        hostKey,
    },
});

/**
 * Načte entity workflow (sady, aktivity, propjení, definiční hráči) a entity Picassa.
 * @param hostKey
 * @param sets Pole všech sad.
 * @param activities Pole všech aktivity zvolené sady.
 * @param transitions Pole všech propojení zvolené sady.
 * @param components Pole picasso komponent.
 * @param links Pole picasso linků.
 * @param definitionParticipants Pole všech definičních hráčů daného workflow.
 * @param selectedSetId Id sady, která má být editována (zobrazena). Když není uvedeno, zůstane aktivní dosavadní sada.
 * @param selectedComponentIndex Index zvolené komponenty.
 * @param selectedLinkIndex Index zvoleného linku.
 */
export const createLoadWorkflowEntitiesAction = (
    hostKey: string,
    sets: readonly ISet[],
    activities: readonly IActivity[],
    transitions: readonly ITransition[],
    components: readonly IComponent[],
    links: readonly ILink[],
    definitionParticipants: readonly IParticipant[],
    selectedSetId?: string,
    selectedComponentIndex?: number,
    selectedLinkIndex?: number,
): ILoadWorkflowEntities => ({
    type: LOAD_WORKFLOW_ENTITIES,
    payload: {
        sets,
        activities,
        transitions,
        components,
        links,
        definitionParticipants,
        selectedComponentIndex,
        selectedLinkIndex,
        selectedSetId,
    },
    meta: {
        hostKey,
        picassoInstanceKey: hostKey,
    },
});

/**
 * Akce updatuje kolekci sad.
 * @param hostKey
 * @param newSets Nově upravené sady.
 */
export const createUpdateSetsAction = (hostKey: string, newSets: readonly ISet[]): IUpdateSetsAction => ({
    type: UPDATE_SETS,
    payload: {
        newSets,
    },
    meta: {
        hostKey,
    },
});

/**
 * Akce updatuje danou aktivitu.
 * @param hostKey
 * @param index Index dané aktivity v poli všech aktivit.
 * @param newActivity Nově upravená aktivita.
 */
export const createUpdateActivityAction = (hostKey: string, picassoIndex: number, newActivity: IActivity): IUpdateActivityAction => ({
    type: UPDATE_ACTIVITY,
    payload: {
        picassoIndex,
        newActivity,
    },
    meta: {
        hostKey,
    },
});

/**
 * Akce updatuje dané propojení.
 * @param hostKey
 * @param index Index daného propojení v poli všech propojení.
 * @param newActivity Nově upravené propojení.
 */
export const createUpdateTransitionAction = (hostKey: string, index: number, newTransition: ITransition): IUpdateTransitionAction => ({
    type: UPDATE_TRANSITION,
    payload: {
        index,
        newTransition,
    },
    meta: {
        hostKey,
    },
});

/**
 * Upraví stav kontejnerů Activity panelu;
 * @param hostKey
 * @param containerName Jméno kontejneru k úpravě.
 * @param isExpanded Určuje otevřenost/zavřenost kontejneru.
 */
export const createUpdateActivityCollapsibleState = (hostKey: string, containerName: ActivityPropertyContainers, isExpanded: boolean): IUpdateActivityPropertyPanelStateAction => ({
    type: UPDATE_ACTIVITY_PROPERTY_PANEL_STATE,
    payload: {
        containerName,
        isExpanded,
    },
    meta: {
        hostKey,
    },
});

/**
 * Upraví stav kontejnerů Transition panelu;
 * @param hostKey
 * @param containerName Jméno kontejneru k úpravě.
 * @param isExpanded Určuje otevřenost/zavřenost kontejneru.
 */
export const createUpdateTransitionCollapsibleState = (hostKey: string, containerName: TransitionPropertyContainers, isExpanded: boolean): IUpdateTransitionPropertyPanelStateAction => ({
    type: UPDATE_TRANSITION_PROPERTY_PANEL_STATE,
    payload: {
        containerName,
        isExpanded,
    },
    meta: {
        hostKey,
    },
});

/**
 * Upraví stav kontejnerů General panelu;
 * @param hostKey
 * @param containerName Jméno kontejneru k úpravě.
 * @param isExpanded Určuje otevřenost/zavřenost kontejneru.
 */
export const createUpdateGeneralCollapsibleState = (hostKey: string, containerName: GeneralPropertyContainer, isExpanded: boolean): IUpdateGeneralPropertyPanelStateAction => ({
    type: UPDATE_GENERAL_PROPERTY_PANEL_STATE,
    payload: {
        containerName,
        isExpanded,
    },
    meta: {
        hostKey,
    },
});

/**
 * Updatuje seznam definičních hráčů pro dané workflow.
 * @param hostKey
 * @param newParticipants Upravená kolekce definičních hráčů.
 * @param newActivities Updatované aktivity (pokud undefined, tak nedojde k aktualizaci referencí hráčů na aktivitách).
 */
export const createUpdateDefinitionParticipants = (hostKey: string, newParticipants: readonly IParticipant[], newActivities?: readonly IActivity[]): IUpdateDefinitionParticipantsAction => ({
    type: UPDATE_DEFINITION_PARTICIPANTS,
    payload: {
        newParticipants,
        newActivities,
    },
    meta: {
        hostKey,
    },
});

/**
 * Zavolá ságu updatující view workflow pro konkrétní sadu. Stará se o překreslení v picassu.
 * @param hostKey
 * @param setId Id sady, která se má zobrazit (edituje se).
 * @param selectedActivityId Id aktivity k označení.
 * @param selectedTransitionId Id propojení k označení.
 * @param isNonEditChange Indikátor, zdali update (ne)způsobuje editační změnu na WF.
 */
export const createUpdateWorkflowViewAction = (hostKey: string, setId: string, selectedActivityId?: string, selectedTransitionId?: string, isNonEditChange: boolean = false): IUpdateWorkflowViewAction => ({
    type: UPDATE_WORKFLOW_VIEW,
    payload: {
        setId,
        selectedActivityId,
        selectedTransitionId,
        isNonEditChange,
    },
    meta: {
        hostKey,
    },
});

/**
 * Akce pro vytvoření propojení u aktivit
 * @param hostKey
 */
export const createCreateTransitionAction = (hostKey: string): ICreateTransitionAction => ({
    type: CREATE_TRANSITION,
    meta: {
        hostKey,
    },
});

/**
 * Akce pro smazání označených propojení a aktivit
 * @param hostKey
 */
export const createDeleteSelectedWfEntitiesAction = (hostKey: string): IDeleteSelectedWfEntitiesAction => ({
    type: DELETE_SELECTED_WF_ENTITIES,
    meta: {
        hostKey,
    },
});

/**
 * Akce pro smazání aktuální sady
 * @param hostKey
 */
export const createDeleteSetAction = (hostKey: string): IDeleteSetAction => ({
    type: DELETE_SET,
    meta: {
        hostKey,
    },
});

/**
 * Zajistí načtení hlavičky pro aktivitu.
 * @param hostKey
 * @param picassoIndex Picasso index aktivity, do které se má přinačíst hlavička.
 * @param wfStateCodes Seznam kódů stavů, jejichž hodnoty se do hlavičky mají načíst.
 */
export const createLoadActivityHeaderAction = (hostKey: string, picassoIndex: number, wfStateCodes: readonly string[]): ILoadActivityHeaderAction => ({
    type: LOAD_ACTIVITY_HEADER,
    payload: {
        picassoIndex,
        wfStateCodes,
    },
    meta: {
        hostKey,
    },
});

/**
 * Zajistí načtení hlavičky pro propojení.
 * @param hostKey
 * @param picassoIndex Picasso index propojení, do které se má přinačíst hlavička.
 * @param wfStateCodes Seznam kódů stavů, jejichž hodnoty se do hlavičky mají načíst.
 */
export const createLoadTransitionHeaderAction = (hostKey: string, picassoIndex: number, wfStateCodes: readonly string[]): ILoadTransitionHeaderAction => ({
    type: LOAD_TRANSITION_HEADER,
    payload: {
        picassoIndex,
        wfStateCodes,
    },
    meta: {
        hostKey,
    },
});

/**
 * Zobrazí message window s prosbou o reakci od uživatele.
 * @param hostKey
 * @param primaryButtonLocKey Lokalizační klíč primary buttonu.
 * @param secondaryButtonLocKey Lokalizační klíč secondary buttonu.
 * @param actionId ID akce, které se zobrazení dialogového okna týka. Slouží pro identifikaci reduxové akce v sáze.
 * @param contentLocKey Lokalizační klíč obsahu.
 */
export const createShowMessageWindowAction = (hostKey: string, primaryButtonLocKey: string, secondaryButtonLocKey: string, actionId: string, contentLocKey: string): IShowMessageWindowAction => ({
    type: SHOW_MESSAGE_WINDOW,
    payload: {
        primaryButtonLocKey,
        secondaryButtonLocKey,
        actionId,
        contentLocKey,
    },
    meta: {
        hostKey,
    },
});

/**
 * Skryje message window.
 * @param hostKey
 */
export const createHideMessageWindowAction = (hostKey: string): IHideMessageWindowAction => ({
    type: HIDE_MESSAGE_WINDOW,
    meta: {
        hostKey,
    },
});

/**
 * Vytvoří akci, informující o tom, že uživatel potvrdil dialog.
 * @param hostKey
 * @param actionId ID akce, které se zobrazení dialogového okna týka. Slouží pro identifikaci reduxové akce v sáze.
 */
export const createDialogConfirmedAction = (hostKey: string, actionId: string): IDialogConfirmedAction => ({
    type: DIALOG_CONFIRMED,
    payload: {
        actionId,
    },
    meta: {
        hostKey,
    },
});

/**
 * Vytvoří akci, informující o tom, že uživatel zrušil dialog.
 * @param hostKey
 * @param actionId ID akce, které se zobrazení dialogového okna týka. Slouží pro identifikaci reduxové akce v sáze.
 */
export const createDialogCanceledAction = (hostKey: string, actionId: string): IDialogCanceledAction => ({
    type: DIALOG_CANCELED,
    payload: {
        actionId,
    },
    meta: {
        hostKey,
    },
});
